import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Activity, CheckCircle2, AlertTriangle, XCircle, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 15000;

/**
 * Data Health widget — small pill in the Dashboard header showing the live
 * status of the market-data pipeline. Click to expand a details popover.
 *
 * Backed by GET /api/data-health which reports:
 *   - status (live | degraded | demo)
 *   - Twelve Data credits used/limit + reset window
 *   - Cache hit rate + entries
 *   - Recent per-symbol source (twelvedata vs demo)
 *   - Last error message (if any)
 */
export default function DataHealthBadge() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const fetchOnce = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/data-health`);
      setData(r.data);
    } catch { /* keep last known state on failure */ }
  }, []);

  useEffect(() => {
    fetchOnce();
    const id = setInterval(fetchOnce, REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchOnce]);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (!data) return null;

  const status = data.status;
  const tone =
    status === "live"     ? { dot: "bg-up",   text: "text-up",   ring: "ring-up/30",   Icon: CheckCircle2, label: "Live",     tip: "Données réelles Twelve Data" } :
    status === "degraded" ? { dot: "bg-warn", text: "text-warn", ring: "ring-warn/30", Icon: AlertTriangle, label: "Dégradé", tip: "Quota API atteint ou erreurs — fallback demo actif" } :
                            { dot: "bg-down", text: "text-down", ring: "ring-down/30", Icon: XCircle,      label: "Demo",     tip: "Aucune clé API — données synthétiques" };

  const td = data.twelve_data;
  const cache = data.cache;
  const creditsLeft = Math.max(0, td.credits_limit - td.credits_used);
  const creditsPct = td.credits_limit > 0 ? (td.credits_used / td.credits_limit) * 100 : 0;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-[11px] font-medium transition-colors ring-1 ring-inset ${tone.ring}`}
        data-testid="data-health-badge"
        title={tone.tip}
        aria-label={`Data health: ${tone.label}`}
      >
        <span className={`w-2 h-2 rounded-full ${tone.dot} ${status === "live" ? "animate-pulse" : ""}`} />
        <Activity size={11} className={tone.text} />
        <span className={tone.text}>{tone.label}</span>
        {td.has_key && (
          <span className="text-white/40 hidden sm:inline">· {creditsLeft}/{td.credits_limit}</span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[340px] max-w-[95vw] rounded-xl border border-white/10 bg-[#0b1420]/98 backdrop-blur-md shadow-2xl z-[60] overflow-hidden"
          data-testid="data-health-popover"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
            <div className="flex items-center gap-2">
              <tone.Icon size={14} className={tone.text} />
              <span className="text-xs font-semibold uppercase tracking-widest">Data Health · {tone.label}</span>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="p-0.5 rounded hover:bg-white/10 text-white/40 hover:text-white" title="Fermer" data-testid="data-health-close">
              <X size={12} />
            </button>
          </div>

          {/* Twelve Data quota */}
          <div className="p-3 border-b border-white/5">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] uppercase tracking-widest text-white/50">Twelve Data · quota / min</span>
              <span className="text-[11px] font-mono tabular-nums text-white/80">
                {td.credits_used} / {td.credits_limit}
              </span>
            </div>
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${creditsPct < 75 ? "bg-up" : creditsPct < 100 ? "bg-warn" : "bg-down"}`}
                style={{ width: `${Math.min(100, creditsPct)}%` }}
                data-testid="data-health-quota-bar"
              />
            </div>
            <div className="flex items-center justify-between mt-1.5 text-[10px] text-white/40">
              <span>{td.has_key ? "Clé API active" : "Aucune clé API"}</span>
              {td.credits_used > 0 && <span>reset dans {td.credits_reset_in_s}s</span>}
            </div>
          </div>

          {/* Cache */}
          <div className="p-3 border-b border-white/5 grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/40">Cache hit</div>
              <div className="text-sm font-mono font-semibold text-up tabular-nums">
                {cache.hit_rate_pct != null ? `${cache.hit_rate_pct}%` : "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/40">Hits</div>
              <div className="text-sm font-mono tabular-nums text-white/80">{cache.hits}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/40">Entrées</div>
              <div className="text-sm font-mono tabular-nums text-white/80">{cache.entries}</div>
            </div>
          </div>

          {/* Recent sources */}
          {data.recent_sources?.length > 0 && (
            <div className="p-3 border-b border-white/5 max-h-40 overflow-y-auto" data-testid="data-health-sources">
              <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5">Sources récentes</div>
              <div className="space-y-1">
                {data.recent_sources.map((s) => (
                  <div key={s.symbol} className="flex items-center justify-between text-[11px]">
                    <span className="font-mono font-semibold text-white/90">{s.symbol}</span>
                    <span className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${s.source === "twelvedata" ? "bg-up" : "bg-down"}`} />
                      <span className={`text-[10px] uppercase tracking-wider ${s.source === "twelvedata" ? "text-up" : "text-down"}`}>
                        {s.source}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Last error */}
          {data.last_error && (
            <div className="p-3 bg-warn/5 border-t border-warn/20 text-[10px] text-warn/80 leading-relaxed" data-testid="data-health-last-error">
              <b className="uppercase tracking-widest text-[9px]">Dernière erreur:</b><br />
              {data.last_error}
            </div>
          )}

          <div className="px-3 py-1.5 text-[9px] text-white/30 text-center bg-white/[0.02]">
            Actualisation auto toutes les 15s · <a href="/audit" className="text-accent-blue hover:underline">chaîne d&apos;audit</a>
          </div>
        </div>
      )}
    </div>
  );
}
